import {useEffect} from 'react';
import {noop} from 'underscore';

import {distribution} from 'metrics/Metrics';

type MeasureUserAgentSpecificMemory = () => Promise<{bytes: number}>;
interface Memory {
  jsHeapSizeLimit: number;
  totalJSHeapSize: number;
  usedJSHeapSize: number;
}
export interface PerformanceVariants extends Performance {
  measureUserAgentSpecificMemory?: MeasureUserAgentSpecificMemory;
  memory?: Memory;
}

type MetricGetter = () => Promise<{suffix: string; value: number}> | {suffix: string; value: number};
type MemoryProfiler = (documentType: string, intervalMs?: number) => void;

const _createUseMemoryProfiler = (metricGetter: MetricGetter): MemoryProfiler => {
  const useMemoryProfiler: MemoryProfiler = (documentType: string, intervalMs: number = 1000) => {
    useEffect(() => {
      const interval = setInterval(async () => {
        const {suffix, value} = await metricGetter();
        const metricName = `perf.notebook.${documentType}.${suffix}`;
        distribution(metricName, value);
      }, intervalMs);
      return () => {
        clearInterval(interval);
      };
    }, [documentType, intervalMs]);
  };
  return useMemoryProfiler;
};

const _createUserAgentSpecificMemoryProfiler = (
  measureUserAgentSpecificMemory: MeasureUserAgentSpecificMemory
): MemoryProfiler =>
  _createUseMemoryProfiler(async () => ({
    suffix: 'bytes_used',
    value: (await measureUserAgentSpecificMemory()).bytes,
  }));

const _createDeprecatedMemoryProfiler = (memory: Memory): MemoryProfiler =>
  _createUseMemoryProfiler(() => ({
    suffix: 'heap_used',
    value: memory.usedJSHeapSize,
  }));

const performance: PerformanceVariants = window.performance as PerformanceVariants;

/**
 * The `useMemoryProfiler` hook is used to measure the memory usage while a Notebook Document is being
 * viewed and/or edited.
 *
 * Browser support for Performance APIs varies. As of early 2025, the `measureUserAgentSpecificMemory` method
 * is experimental, while the `memory` property is deprecated. To collect memory usage data on a best-effort
 * basis, we use the `measureUserAgentSpecificMemory` method if it is available (and the browser is
 * cross-origin isolated), otherwise falling back to the `memory` property if it is available, and finally
 * skipping metrics collection altogether if neither is available.
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/Performance/measureUserAgentSpecificMemory
 * https://developer.mozilla.org/en-US/docs/Web/API/Performance/memory
 *
 * @param documentType The type of document being viewed
 * @param intervalMs The interval at which to collect memory usage data (default: 1000ms)
 */
export const useMemoryProfiler = (documentType: string, intervalMs?: number): void => {
  const _useMemoryProfiler =
    performance?.measureUserAgentSpecificMemory && window.crossOriginIsolated
      ? _createUserAgentSpecificMemoryProfiler(performance.measureUserAgentSpecificMemory)
      : performance?.memory
        ? _createDeprecatedMemoryProfiler(performance.memory)
        : noop;
  _useMemoryProfiler(documentType, intervalMs);
};
